import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  BoxProps,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  SwitchProps,
  Table,
  styled,
  Theme,
} from "@mui/material";
import { paletteVar, TextField, Typography } from "@platform/design-system";
import {
  Proposal as AquafinaProposal,
  Coverage,
  PolicyVariants,
  ProposalDetail,
} from "@platform/view-policy-components";
import { useMemo } from "react";

import {
  MARKETING_PRESENTATION_REASON_LABELS,
  OTHER_REASON,
  REQUOTE_REASONS,
} from "../constants";
import {
  DentalProductType,
  FormState,
  PlanType,
  ProductType,
  Proposal,
  SetFormState,
} from "../types";
import { formatDate } from "../utils";

type Props = {
  formState: FormState;
  setFormState: SetFormState;
  aquafinaProposal?: AquafinaProposal | null;
  quoteProposal: Proposal;
  productType: ProductType;
  clientName: string;
  productPlanType: PlanType;
  loadingRefetch: boolean;
  onRefetch: VoidFunction;
  displayV4Fields: boolean;
};

const mapperVariants: Map<ProductType, PolicyVariants> = new Map([
  ["ltd", PolicyVariants.LTD],
  ["std", PolicyVariants.STD],
  ["vision", PolicyVariants.VISION],
  ["medical", PolicyVariants.MEDICAL],
  ["basicLife", PolicyVariants.BASIC_LIFE],
  ["voluntaryLife", PolicyVariants.VOLUNTARY_LIFE],
  ["hospitalIndemnity", PolicyVariants.HOSPITAL_INDEMNITY],
  ["accident", PolicyVariants.ACCIDENT],
  ["criticalIllness", PolicyVariants.CRITICAL_ILLNESS],
]);

const dentalMapperVariants: Map<DentalProductType, PolicyVariants> = new Map([
  ["dental_ppo", PolicyVariants.DENTAL_PPO],
  ["dental_hmo", PolicyVariants.DENTAL_HMO],
  ["dental_epo", PolicyVariants.DENTAL_EPO],
  ["dental_pos", PolicyVariants.DENTAL_POS],
  ["dental_indemnity", PolicyVariants.DENTAL_INDEMNITY],
]);

const sourceMapper: Record<string, string> = {
  api: "API",
  ida: "SortSpoke",
};

const resolveVariant = (
  productType: ProductType,
  productPlanType: PlanType,
): PolicyVariants | undefined => {
  if (productType === "dental" && productPlanType) {
    return dentalMapperVariants.get(`${productType}_${productPlanType}`);
  }
  return mapperVariants.get(productType);
};

function Form({
  aquafinaProposal,
  quoteProposal,
  formState,
  setFormState,
  productType,
  productPlanType,
  clientName,
  loadingRefetch,
  onRefetch,
  displayV4Fields,
}: Props): React.JSX.Element {
  const carrierName = aquafinaProposal?.carrierName;

  const aquafinaCoverageField = useMemo(
    () => (productType === "voluntaryLife" ? "volLife" : productType),
    [productType],
  );

  const proposalDetailVariant = resolveVariant(productType, productPlanType);

  if (!proposalDetailVariant) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "100%" }}>
        <Typography variant="header-large" color="danger">
          No Information Available
        </Typography>
      </Box>
    );
  }

  const coverage = aquafinaProposal
    ? (aquafinaProposal[
        aquafinaCoverageField as keyof AquafinaProposal
      ] as Coverage | null)
    : null;

  const toggleRequoteNeeded = (value: boolean) => {
    setFormState("requoteReason", "");
    setFormState("requoteNeeded", value);
  };

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          bgcolor: "neutrals.neutral_50",
          marginBottom: 2.75,
          p: 3,
          gap: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography>
              <strong>{clientName}</strong>
            </Typography>
            {carrierName && (
              <Typography>
                <Box component="span" fontWeight={600}>
                  Carrier:
                </Box>{" "}
                {carrierName}
              </Typography>
            )}
            {coverage?.name && (
              <Typography>
                <Box component="span" fontWeight={600}>
                  Coverage:
                </Box>{" "}
                {coverage.name}
              </Typography>
            )}
            <Typography>
              <Box component="span" fontWeight={600}>
                Source:
              </Box>{" "}
              {quoteProposal.dataSource
                ? sourceMapper[quoteProposal.dataSource]
                : sourceMapper.api}
            </Typography>
          </Box>
          {aquafinaProposal && (
            <Box>
              <RefreshButton onRefetch={onRefetch} loading={loadingRefetch} />
            </Box>
          )}
        </Box>
        {quoteProposal?.proposalDate && (
          <Typography>
            <Box component="span" fontWeight={600}>
              Date Received:
            </Box>{" "}
            {formatDate(quoteProposal.proposalDate, true)}
          </Typography>
        )}
      </Paper>
      {aquafinaProposal && (
        <Table stickyHeader sx={{ mt: 4 }}>
          <ProposalDetail
            proposalDetail={aquafinaProposal}
            variant={proposalDetailVariant}
            displayV4Fields={displayV4Fields}
          />
        </Table>
      )}
      <ColoredDivider my={3} component="hr" />
      <Box rowGap={4}>
        <Box p={1} borderRadius={2} display="flex">
          <Typography variant="body-large" gutterBottom>
            <strong>Quote Review</strong>
          </Typography>
        </Box>
        <Box p={1} borderRadius={2} display="flex" columnGap={10}>
          <Box
            display="flex"
            flexDirection="column"
            rowGap={2}
            alignItems="flex-start"
            mb={2}
          >
            <Box display="flex">
              <FormControlLabel
                control={
                  <CustomSwitch
                    checked={formState.requoteNeeded}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      toggleRequoteNeeded(e.target.checked)
                    }
                  />
                }
                label={""}
                labelPlacement="bottom"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: 12,
                    width: "100%",
                    textAlign: formState.requoteNeeded ? "left" : "right",
                  },
                  marginLeft: "0px !important",
                }}
              />
              <FormLabel id="marketing-presentation-label">
                Requote Needed
              </FormLabel>
            </Box>
            {formState.requoteNeeded && (
              <FormControl sx={{ minWidth: "15.313rem" }} size="small">
                <InputLabel id="marketing-presentation-exclude-reason">
                  Reason{" "}
                  <Typography color="danger" component="span">
                    *
                  </Typography>
                </InputLabel>
                <Select
                  labelId="marketing-presentation-exclude-reason"
                  value={formState.requoteReason}
                  onChange={(e) => {
                    setFormState({
                      requoteReason: e.target.value,
                      requoteOtherReason: "",
                    });
                  }}
                  label="Reason"
                  IconComponent={ExpandMoreIcon}
                >
                  <MenuItem value="" disabled>
                    Reason
                  </MenuItem>
                  {Object.entries(REQUOTE_REASONS).map(([key, value]) => (
                    <MenuItem value={key} key={key}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {formState.requoteReason === "other" && (
              <FormControl>
                <TextField
                  label="Other Reason"
                  value={formState.requoteOtherReason}
                  required
                  autoComplete="off"
                  onChange={(e) =>
                    setFormState("requoteOtherReason", e.target.value)
                  }
                />
              </FormControl>
            )}
          </Box>
          <Box display="flex" flexDirection="column" rowGap={2}>
            <FormControl>
              <FormLabel id="marketing-presentation-label">
                Marketing Presentation
              </FormLabel>
              <RadioGroup
                aria-labelledby="marketing-presentation-label"
                name="marketing-presentation"
                value={formState.includeInMarketingPresentation}
                onChange={(e) => {
                  setFormState(
                    "includeInMarketingPresentation",
                    e.target.value === "true",
                  );
                  setFormState("marketingTemplateExcludeReason", "");
                }}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Included"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Excluded"
                />
              </RadioGroup>
            </FormControl>
            {!formState.includeInMarketingPresentation && (
              <FormControl sx={{ minWidth: "15.313rem" }} size="small">
                <InputLabel id="marketing-presentation-exclude-reason">
                  Reason{" "}
                  <Typography color="danger" component="span">
                    *
                  </Typography>
                </InputLabel>
                <Select
                  labelId="marketing-presentation-exclude-reason"
                  value={formState.marketingTemplateExcludeReason}
                  onChange={(e) => {
                    setFormState({
                      marketingTemplateExcludeReason: e.target.value,
                      marketingTemplateExcludeOtherReason: "",
                    });
                  }}
                  label="Reason"
                  IconComponent={ExpandMoreIcon}
                >
                  <MenuItem value="" disabled>
                    Reason
                  </MenuItem>
                  {Object.entries(MARKETING_PRESENTATION_REASON_LABELS).map(
                    ([key, value]) => (
                      <MenuItem value={key} key={key}>
                        {value}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            )}
            {formState.marketingTemplateExcludeReason === OTHER_REASON && (
              <FormControl>
                <TextField
                  label="Other Reason"
                  required
                  autoComplete="off"
                  value={formState.marketingTemplateExcludeOtherReason}
                  onChange={(e) =>
                    setFormState(
                      "marketingTemplateExcludeOtherReason",
                      e.target.value,
                    )
                  }
                />
              </FormControl>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

const RefreshButton = ({
  onRefetch,
  loading,
}: {
  onRefetch: VoidFunction;
  loading: boolean;
}) => (
  <Button
    variant="outlined"
    color="brandPrimary"
    onClick={onRefetch}
    loading={loading}
    loadingPosition="start"
    startIcon={<RefreshIcon />}
  >
    {loading ? "Refreshing" : "Refresh"}
  </Button>
);

const ColoredDivider = styled((props: BoxProps) => <Box {...props} />)(() => ({
  background:
    "linear-gradient(89.89deg, #832A80 38.88%, #0078A2 105.32%, #4FB3CD 139.34%)",
  height: 4,
  border: "none",
}));

const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }: { theme: Theme }) => ({
  width: 48,
  height: 24,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(23px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: paletteVar("secondary-main", "#0078A2"),
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: paletteVar("secondary-main", "#0078A2"),
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20,
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:before": {
      content: '""',
      display: "none",
    },
  },
  "& .Mui-checked .MuiSwitch-thumb": {
    "&:before": {
      content: '"✓"',
      display: "block",
      color: "#fff",
      fontSize: 16,
      left: "-14px",
      position: "absolute",
    },
  },
  "& .MuiSwitch-track": {
    borderRadius: 24 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default Form;
